import clsx from 'clsx'

import {
  SENTIMENT_LABELS,
  normalizeSentiment,
  signedScore
} from '@/common/app/modules/media/sentiment'
import { BrandSentimentItem, SentimentLabel } from '@/common/model/media/sentiment'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

interface Props {
  items: BrandSentimentItem[]
}

// センチメント別のカード枠線・スコア面の配色
const toneStyle = (
  sentiment: SentimentLabel
): { card: string; scoreArea: string; scoreText: TextColor } => {
  switch (sentiment) {
    case 'positive':
      return {
        card: 'border-success',
        scoreArea: 'bg-fill-success',
        scoreText: TextColor.ON_BG_FILL
      }
    case 'negative':
      return {
        card: 'border-critical',
        scoreArea: 'bg-fill-critical',
        scoreText: TextColor.ON_BG_FILL
      }
    default:
      return {
        card: 'border-primary',
        scoreArea: 'bg-fill-tertiary',
        scoreText: TextColor.PRIMARY
      }
  }
}

// ラベル付きの1行（トーン・ポジティブ要素・ネガティブ要素）
const LabeledRow = ({
  label,
  badgeType,
  texts
}: {
  label: string
  badgeType: BadgeType
  texts: string[]
}) => (
  <ItemAlign gapX={8} align='baseline'>
    <Badge size={10} badgeType={badgeType} fixed>
      {label}
    </Badge>
    <Text size={12} lineHeight={150}>
      {texts.length > 0 ? texts.join(' / ') : '特になし'}
    </Text>
  </ItemAlign>
)

// プロンプト別結果（質問ごとのセンチメントと回答の要素）
const SentimentPromptList = ({ items }: Props) => (
  <GeoSection title='プロンプト別結果'>
    <div className='flex flex-col gap-3'>
      {items.map((item, i) => {
        const sentiment = normalizeSentiment(item.overall_sentiment)
        const tone = toneStyle(sentiment)

        return (
          <div
            key={i}
            className={clsx(
              'bg-surface flex items-stretch overflow-hidden rounded-lg border p-1',
              tone.card
            )}
          >
            {/* センチメントとスコア */}
            <div
              className={clsx(
                'flex w-20 shrink-0 flex-col items-center justify-center gap-0.5 rounded-md px-2 py-3',
                tone.scoreArea
              )}
            >
              <Text size={10} color={tone.scoreText}>
                {SENTIMENT_LABELS[sentiment]}
              </Text>
              <Text size={16} weight='bold' color={tone.scoreText}>
                {signedScore(item.sentiment_score)}
              </Text>
            </div>
            {/* 質問と回答の要素 */}
            <div className='flex flex-1 flex-col gap-1 px-4 py-2'>
              <Text size={14} weight='bold'>
                {item.prompt}
              </Text>
              {item.response_summary && (
                <LabeledRow
                  label='トーン'
                  badgeType={BadgeType.SQUARE_TERTIARY}
                  texts={[item.response_summary]}
                />
              )}
              <LabeledRow
                label='ポジティブ要素'
                badgeType={BadgeType.SUCCESS}
                texts={item.positive_points ?? []}
              />
              <LabeledRow
                label='ネガティブ要素'
                badgeType={BadgeType.CRITICAL}
                texts={item.negative_points ?? []}
              />
            </div>
          </div>
        )
      })}
    </div>
  </GeoSection>
)

export default SentimentPromptList
